import React, { useEffect, useState, useRef } from "react";
import {
  FaDoorOpen,
  FaUtensils,
  FaGlassMartiniAlt,
  FaFilm,
  FaStar,
  FaMapMarkerAlt,
  FaClock,
  FaChevronLeft,
  FaChevronRight, FaBuilding, FaUmbrellaBeach, FaDumbbell
} from "react-icons/fa";
import { motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import { Link } from "react-router-dom";

// Custom components
const GoldAccent = ({ className = "" }) => (
  <div
    className={`h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent ${className}`}
  ></div>
);

const GoldDivider = () => (
  <div className="flex items-center justify-center my-12">
    <div className="w-16 h-px bg-amber-700"></div>
    <div className="mx-3">
      <div className="w-3 h-3 rounded-full bg-gradient-to-r from-amber-400 to-amber-600 transform rotate-45"></div>
    </div>
    <div className="w-16 h-px bg-amber-700"></div>
  </div>
);

const PremiumButton = ({ children, secondary = false, small = false }) => (
  <button
    className={`
    ${small ? "px-6 py-2 text-sm" : "px-8 py-4"} 
    ${
      secondary
        ? "bg-transparent text-amber-400 hover:bg-amber-400/10"
        : "bg-gradient-to-r from-amber-500 to-amber-700 hover:from-amber-600 hover:to-amber-800 text-white"
    } 
    rounded-sm uppercase tracking-widest font-medium
    transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-amber-500/20
    relative overflow-hidden group flex items-center justify-center
  `}
  >
    <span
      className="absolute inset-0 w-full h-full bg-gradient-to-r from-amber-400/0 via-white/20 to-amber-400/0 
      -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
    ></span>
    {children}
  </button>
);

// Premium Service Card with parallax effect
const ServiceCard = ({
  title,
  subtitle,
  description,
  image,
  icon,
  reversed,
  index,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: index * 0.2 }}
      className={`flex flex-col ${
        reversed ? "lg:flex-row-reverse" : "lg:flex-row"
      } mb-32 group relative`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-aos="fade-up"
      data-aos-duration="1000"
    >
      {/* Text Section */}
      <div className="w-full lg:w-1/2 relative z-10">
        <div className="p-6 sm:p-8 md:p-12 lg:p-16 flex flex-col justify-center">
          <div className="mb-6 flex items-center">
            <div className="w-12 h-12 rounded-sm flex items-center justify-center bg-amber-400/10">
              {icon}
            </div>
            <div className="ml-4">
              <PremiumButton secondary small>
                <span>{subtitle}</span>
                <span className="ml-2 transform group-hover:translate-x-1 transition-transform duration-300">
                  →
                </span>
              </PremiumButton>
            </div>
          </div>

          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-white leading-tight">
            {title}
          </h3>

          <div className="w-16 h-0.5 bg-gradient-to-r from-amber-400 to-amber-600 mb-6"></div>

          <p className="text-gray-300 text-base leading-relaxed">
            {description}
          </p>
        </div>
      </div>

      {/* Image Section */}
      <div className="w-full lg:w-1/2 relative aspect-[4/3] lg:aspect-auto h-64 lg:h-auto overflow-hidden rounded-xl shadow-lg">
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-black/50 to-transparent z-10"
          animate={{ opacity: isHovered ? 0.4 : 0.7 }}
          transition={{ duration: 0.5 }}
        />
        <motion.img
          src={image}
          alt={title}
          className="w-full h-full object-cover"
          animate={{
            scale: isHovered ? 1.05 : 1,
            filter: isHovered ? "brightness(1.05)" : "brightness(0.9)",
          }}
          transition={{ duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
        />

        {/* Info Badge */}
        <div className="absolute bottom-0 left-0 p-4 z-20 text-white">
          <div className="flex items-center mb-1">
            {[...Array(5)].map((_, i) => (
              <FaStar key={i} className="text-amber-400 mr-1 text-sm" />
            ))}
          </div>
          <div className="flex text-white/80 text-xs space-x-4">
            <div className="flex items-center">
              <FaMapMarkerAlt className="mr-1 text-amber-400" />
              <span>Main Wing</span>
            </div>
            <div className="flex items-center">
              <FaClock className="mr-1 text-amber-400" />
              <span>24/7</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Stats Display
const StatsSection = () => {
  const stats = [
    { value: "5⭐", label: "Luxury Rating" },
    { value: "24/7", label: "Premium Service" },
    { value: "100%", label: "Guest Satisfaction" },
    { value: "20+", label: "Years of Excellence" },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12">
      {stats.map((stat, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: index * 0.1 }}
          className="flex flex-col items-center p-6 bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-sm"
        >
          <span className="text-3xl md:text-4xl font-bold text-amber-400 mb-2">
            {stat.value}
          </span>
          <GoldAccent className="w-10 mb-2" />
          <span className="text-gray-300 text-sm uppercase tracking-wide text-center">
            {stat.label}
          </span>
        </motion.div>
      ))}
    </div>
  );
};

// New Brand Partners Section
const BrandPartnersSection = () => {
  const brands = [
    {
      name: "KFC",
      logo: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/WhatsApp%20Image%202025-06-05%20at%2016.33.11%20(1).jpeg?updatedAt=1749122369938",
      description: "Finger Lickin' Good"
    },
    {
      name: "Barista",
      logo: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/PHOTO-2025-06-05-17-27-21(1).jpg?updatedAt=1749124658758",
      description: "Premium Coffee Experience"
    },
    {
      name: "Pizza Hut",
      logo: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/WhatsApp%20Image%202025-06-05%20at%2016.33.13%20(1).jpeg?updatedAt=1749122369971",
      description: "Make it Great"
    },
    {
      name: "Vango",
      logo: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/WhatsApp%20Image%202025-06-05%20at%2016.33.12%20(3).jpeg?updatedAt=1749122369912",
      description: "Authentic Indian Flavors"
    }
  ];

  return (
    <div className="pb-10 relative">
      <div className="text-center mb-16" data-aos="fade-up">
        <span className="text-amber-400 uppercase tracking-widest text-sm font-medium mb-4 block">
          Culinary Partners
        </span>
        <h3 className="text-3xl md:text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-amber-300 to-yellow-500">
          World-Class Food Brands
        </h3>
        <GoldAccent className="w-24 mx-auto mb-6" />
        <p className="text-gray-300 max-w-3xl mx-auto text-lg">
          Experience the finest culinary offerings from globally recognized brands, 
          all conveniently located within The Gulmohar Grand
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 px-4">
        {brands.map((brand, index) => (
          <motion.div
            key={brand.name}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className="group relative"
            data-aos="fade-up"
            data-aos-delay={index * 100}
          >
            <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-6 text-center relative overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-amber-500/10 hover:border-amber-500/30 hover:-translate-y-2">
              
              {/* Animated background gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 via-amber-500/5 to-amber-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              
              {/* Gold corner accents */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-amber-500/20 group-hover:border-amber-500/60 transition-colors duration-500"></div>
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-amber-500/20 group-hover:border-amber-500/60 transition-colors duration-500"></div>
              
              {/* Logo container */}
              <div className="relative z-10 mb-6">
                <div className="w-26 h-40 mx-auto mb-4 rounded-lg flex items-center justify-center overflow-hidden shadow-lg group-hover:shadow-xl transition-all duration-500 group-hover:scale-105">
                  <img
                    src={brand.logo}
                    alt={`${brand.name} logo`}
                    className="max-w-full max-h-full object-contain p-2 transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                
                {/* Brand name */}
                <h4 className="text-xl font-bold text-white mb-2 group-hover:text-amber-300 transition-colors duration-300">
                  {brand.name}
                </h4>
                
                {/* Gold divider */}
                <div className="w-12 h-0.5 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto mb-3 group-hover:w-16 transition-all duration-500"></div>
                
                {/* Description */}
                <p className="text-gray-400 text-sm group-hover:text-gray-300 transition-colors duration-300">
                  {brand.description}
                </p>
              </div>

              {/* Shine effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-1000">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1500"></div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Bottom decorative text */}
      <div className="text-center mt-16" data-aos="fade-up" data-aos-delay="400">
        <p className="text-gray-400 italic max-w-2xl mx-auto">
          "Where global flavors meet luxury hospitality - experience the finest culinary journey 
          with world-renowned brands under one magnificent roof."
        </p>
        <div className="flex justify-center items-center space-x-1 mt-4">
          {[...Array(5)].map((_, i) => (
            <FaStar key={i} className="text-amber-400 text-sm" />
          ))}
        </div>
      </div>
    </div>
  );
};

const GallerySection = () => {
  const images = [
    "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/WhatsApp%20Image%202025-06-05%20at%2016.33.12%20(2).jpeg?updatedAt=1749122370021",
    "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/WhatsApp%20Image%202025-06-05%20at%2016.33.13%20(1).jpeg?updatedAt=1749122369971",
    "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/WhatsApp%20Image%202025-06-05%20at%2016.33.12%20(1).jpeg?updatedAt=1749122370054",
    "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/WhatsApp%20Image%202025-06-05%20at%2016.33.11%20(1).jpeg?updatedAt=1749122369938",
    "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/WhatsApp%20Image%202025-06-05%20at%2016.33.12%20(3).jpeg?updatedAt=1749122369912",
    "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/WhatsApp%20Image%202025-06-05%20at%2016.33.12.jpeg?updatedAt=1749122369985",
    "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/WhatsApp%20Image%202025-06-05%20at%2016.33.14%20(2).jpeg?updatedAt=1749122374736",
    "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/WhatsApp%20Image%202025-06-05%20at%2016.33.14.jpeg?updatedAt=1749122374455",
    "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/WhatsApp%20Image%202025-06-05%20at%2016.33.14%20(1).jpeg?updatedAt=1749122373540",
    "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/WhatsApp%20Image%202025-06-05%20at%2016.33.13.jpeg?updatedAt=1749122370010",
    "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/WhatsApp%20Image%202025-06-05%20at%2016.33.11.jpeg?updatedAt=1749122369539",
    "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/PHOTO-2025-06-05-17-27-21.jpg?updatedAt=1749124658721",
  ];

  const galleryRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);

  const scroll = (direction) => {
    if (galleryRef.current) {
      const { scrollWidth, clientWidth } = galleryRef.current;
      const scrollAmount = clientWidth * 0.8;
      const maxScroll = scrollWidth - clientWidth;

      let newPosition;
      if (direction === "left") {
        newPosition = Math.max(0, scrollPosition - scrollAmount);
      } else {
        newPosition = Math.min(maxScroll, scrollPosition + scrollAmount);
      }

      galleryRef.current.scrollTo({ left: newPosition, behavior: "smooth" });
      setScrollPosition(newPosition);
    }
  };

  return (
    <div className="py-16 relative">
      <div className="text-center mb-10" data-aos="fade-up">
        <h3 className="text-2xl md:text-3xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-amber-300 to-yellow-500">
          The Food Street
        </h3>
        <p className="text-gray-300 max-w-2xl mx-auto">
          Glimpses of the extraordinary food street at Gulmohar Grand
        </p>
      </div>

      <div className="relative px-4 md:px-10">
        <button
          onClick={() => scroll("left")}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-black/80 text-white w-10 h-10 rounded-full flex items-center justify-center shadow-lg transform transition-all hover:scale-110"
        >
          <FaChevronLeft />
        </button>

        <div
          ref={galleryRef}
          className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory gap-4 pb-8"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {images.map((img, i) => (
            <div
              key={i}
              className="min-w-[280px] md:min-w-[400px] h-64 rounded-lg overflow-hidden snap-center flex-shrink-0 relative group"
            >
              <img
                src={img}
                alt={`Gallery ${i + 1}`}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end">
                <div className="p-4">
                  <p className="text-white font-medium">
                    Gulmohar Experience {i + 1}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={() => scroll("right")}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-black/80 text-white w-10 h-10 rounded-full flex items-center justify-center shadow-lg transform transition-all hover:scale-110"
        >
          <FaChevronRight />
        </button>
      </div>
    </div>
  );
};

const Services = () => {
  const [mounted, setMounted] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    AOS.init({
      once: true,
      offset: 100,
    });
    setMounted(true);

    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const services = [
    {
      id: 1,
      title: "THE TANDOOR FLAMES",
      subtitle: "Fine Dine Restaurant",
      description:
        "A fine culinary voyage awaits your arrival at THE TANDOORI FLAMES. Choose from the many flavours and aromas at our stylish dining restaurant serving delectable cuisines. The state-of-the-art garnishing done by our chefs is a highlight our guests love to indulge in while dining at THE TANDOORI FLAMES.",
      image:
        "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/PICTURES/PROPERTY%20SHOOT/02/0N2A6046.JPG",
      icon: <FaUtensils className="text-amber-400 text-3xl" />,
    },
    {
      id: 2,
      title: "THE GULMOHAR BAR",
      subtitle: "Premium Bar Experience",
      description:
        "Step Inside the amazing ambience of our GRAND BAR, found within the heart of The Gulmohar Grand where our artistic bartenders prepare enchanting cocktails which change colour and smoke at your table.",
      image:
        "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/PICTURES/PROPERTY%20SHOOT/01/0N2A9969.JPG",
      icon: <FaGlassMartiniAlt className="text-amber-400 text-3xl" />,
    },
    {
      id: 3,
      title: "GRAND CINEMAZ",
      subtitle: "Premium Movie Experience",
      description:
        '"Cinema is life with all the boring things cut" – Anonymous. Our multiplex provides the finest seating arrangements to make you feel comfortable, like at home.',
      image:
        "https://ik.imagekit.io/tdlebsr5e/grand-cinemaz.jpg?updatedAt=1747479361078",
      icon: <FaFilm className="text-amber-400 text-3xl" />,
    },
  {
    id: 4,
    title: "GRAND SENATE HALL",
    subtitle: "100 Persons Capacity",
    description:
      "Host corporate meetings, seminars, and intimate gatherings at our GRAND SENATE HALL — a perfect blend of elegance and modern amenities with seating for up to 100 guests.",
    image:
        "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/FINAL%20EDITED%20GULMOHAR/WhatsApp%20Image%202025-05-16%20at%2015.46.38.jpeg?updatedAt=1747396493799",
    icon: <FaBuilding className="text-amber-400 text-3xl" />,
  },
  {
    id: 5,
    title: "THE GULMOHAR INFINITY BANQUET",
    subtitle: "600 Persons Capacity",
    description:
      "Celebrate your grand events at THE GULMOHAR INFINITY BANQUET, offering a luxurious space that accommodates up to 600 guests with supreme hospitality and ambiance.",
    image:
        "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/PICTURES/EVENTS/SILVER%20JUBLEE%20EVENT/0N2A1722.JPG",
    icon: <FaBuilding className="text-amber-400 text-3xl" />,
  },
  {
  id: 6,
  title: "THE GULMOHAR GRAND SKY LOUNGE",
  subtitle: "Infinity Rooftop Pool",
  description:
    "Dive into serenity at THE GULMOHAR GRAND SKY LOUNGE — our stunning infinity rooftop pool offering panoramic skyline views, luxurious seating, and a refreshing escape above the city.",
  image:
        "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/FINAL%20EDITED%20GULMOHAR/WhatsApp%20Image%202025-05-16%20at%2015.47.57.jpeg?updatedAt=1747396493676",
  icon: <FaUmbrellaBeach className="text-amber-400 text-3xl" />,
},
  {
    id: 7,
    title: "GRAND GYM",
    subtitle: "Fitness & Wellness Center",
    description:
      "Stay energized and fit with our GRAND GYM, featuring state-of-the-art fitness equipment, personal training, and wellness facilities for all guests.",
    image:
        "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/PICTURES/PROPERTY%20SHOOT/01/0N2A0055.JPG",
    icon: <FaDumbbell className="text-amber-400 text-3xl" />,
  },
  ];

  const testimonials = [
    {
      id: 1,
      content:
        "The Tandoori Flames offers one of the finest dining experiences I've ever had. The attention to detail in flavor and presentation is truly remarkable.",
      author: "Alexander Thompson",
      position: "Food Critic",
    },
    {
      id: 2,
      content:
        "My evening at the Grand Bar was nothing short of magical. The mixologists are true artists, crafting drinks that delight all the senses.",
      author: "Sophia Chen",
      position: "Travel Blogger",
    },
    {
      id: 3,
      content:
        "Grand Cinemaz combines comfort and luxury in a way that transforms movie watching into a premium experience. A must-visit for film enthusiasts.",
      author: "Rafael Mendoza",
      position: "Film Director",
    },
  ];

  return (
    <div className="bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 min-h-screen text-white">
      <div className="max-w-6xl mx-auto px-4 py-16 md:py-24">
        {/* Introduction */}
        <section
          className="mb-20 md:mb-32 text-center max-w-4xl mx-auto"
          data-aos="fade-up"
        >
          <span className="text-amber-400 uppercase tracking-widest text-sm font-medium mb-4 block">
            The Gulmohar Experience
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-amber-300 to-amber-500">
              Unparalleled Luxury Services
            </span>
          </h2>

          <GoldDivider />

          <p className="text-gray-300 text-lg md:text-xl leading-relaxed mb-12">
            At Gulmohar Grand, we believe in creating not just memories, but
            experiences that transcend the ordinary. Our signature services are
            crafted with precision, passion, and an unwavering commitment to
            excellence.
          </p>

          <StatsSection />
        </section>

        {/* Services */}
        <section className="mb-32">
          <div className="mb-20 text-center">
            <span
              className="text-amber-400 uppercase tracking-widest text-sm font-medium mb-4 block"
              data-aos="fade-up"
            >
              Curated For You
            </span>
            <h2
              className="text-3xl md:text-4xl font-bold mb-6"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              Signature Services
            </h2>
            <GoldAccent
              className="w-24 mx-auto"
              data-aos="fade-up"
              data-aos-delay="200"
            />
          </div>

          <div className="space-y-24">
            {services.map((service, index) => (
              <ServiceCard
                key={service.id}
                title={service.title}
                subtitle={service.subtitle}
                description={service.description}
                image={service.image}
                icon={service.icon}
                reversed={index % 2 !== 0}
                index={index}
              />
            ))}
          </div>
        </section>

        {/* Brand Partners Section - NEW */}
        <BrandPartnersSection />

        {/* Gallery Section */}
        <GallerySection />

        {/* Footer Section */}
        <div
          className="bg-gradient-to-r from-gray-800 to-gray-900 p-8 md:p-12 rounded-xl shadow-2xl border border-gray-700 text-center relative overflow-hidden group"
          data-aos="fade-up"
          data-aos-delay="300"
        >
          {/* Animated overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-amber-600/0 via-amber-600/5 to-amber-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

          {/* Gold corners */}
          <div className="absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-amber-500/30"></div>
          <div className="absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 border-amber-500/30"></div>
          <div className="absolute bottom-0 left-0 w-12 h-12 border-b-2 border-l-2 border-amber-500/30"></div>
          <div className="absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-amber-500/30"></div>

          <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white relative z-10">
            A Symphony of Tastes at The Food Street
          </h3>
          <p className="text-gray-300 mb-8 max-w-3xl mx-auto relative z-10">
            The Food Street at The Gulmohar Grand is more than just a food
            court; it's a symphony of tastes, textures, and aromas. Whether
            you're seeking comfort food, exploring global cuisines, or simply
            enjoying a cup of coffee with friends, our diverse portfolio of
            iconic global and successful in-house brands ensures there's
            something for every palate.
          </p>
          <p className="text-gray-300 mb-8 max-w-3xl mx-auto relative z-10">
            Join us at The Food Street, proudly presented by Devyani
            International Limited, where culinary excellence meets a vibrant
            atmosphere, creating memories one bite at a time. Indulge in the
            finest offerings from KFC, Pizza Hut, Costa Coffee, and Vaango—all
            under one roof at The Gulmohar Grand.
          </p>

          <div className="flex justify-center items-center space-x-2 text-amber-400">
            {[...Array(5)].map((_, i) => (
              <FaStar key={i} className="text-amber-400" />
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div
          className="mt-16 bg-gradient-to-r from-gray-800 to-gray-900 p-8 md:p-16 rounded-xl shadow-2xl border border-gray-700 text-center"
          data-aos="fade-up"
          data-aos-delay="300"
        >
          <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white">
            Ready to Experience Luxury?
          </h3>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Book your stay at Gulmohar Grand and indulge in our premium services
            for an unforgettable experience.
          </p>
          <Link to="/room-booking">
            <button className="px-8 py-4 rounded-full bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-700 hover:to-amber-600 text-white font-semibold transition-all shadow-lg hover:shadow-amber-500/30 transform hover:-translate-y-1 duration-300">
              Book Your Experience Now
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Services;
